import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { FEEDBACK_STATUSES, feedbackCategoryLabel, feedbackPriorityLabel, feedbackSubmitToLabel } from '../../data/feedbackConstants';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import { RefreshCw } from 'lucide-react';

export default function MyFeedbackList() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });

  const fetchItems = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get('/feedback/my', { params });
      setItems(res.data?.data?.items || []);
      setMeta(res.data?.data?.meta || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(1); }, [statusFilter]);

  return (
    <div className="space-y-6" data-testid="my-feedback-list">
      <div>
        <h1 className="text-2xl font-bold text-foreground">My Feedback</h1>
        <p className="text-muted-foreground">Track submissions and admin responses</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>Submissions</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-48"
              onKeyDown={(e) => e.key === 'Enter' && fetchItems(1)}
            />
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {FEEDBACK_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => fetchItems(meta.page)} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="text-muted-foreground py-8 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">No feedback submitted yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-3 pr-4 font-medium">Feedback ID</th>
                  <th className="pb-3 pr-4 font-medium">Title</th>
                  <th className="pb-3 pr-4 font-medium">Submit to</th>
                  <th className="pb-3 pr-4 font-medium">Category</th>
                  <th className="pb-3 pr-4 font-medium">Priority</th>
                  <th className="pb-3 pr-4 font-medium">Status</th>
                  <th className="pb-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 hover:bg-muted/40">
                    <td className="py-3 pr-4">
                      <Link to={`/my-feedback/${row.id}`} className="font-mono text-blue-600 hover:underline">
                        {row.feedback_code}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 max-w-[200px] truncate">{row.title}</td>
                    <td className="py-3 pr-4">{feedbackSubmitToLabel(row.submit_to)}</td>
                    <td className="py-3 pr-4">{feedbackCategoryLabel(row.category)}</td>
                    <td className="py-3 pr-4 capitalize">{feedbackPriorityLabel(row.priority)}</td>
                    <td className="py-3 pr-4"><FeedbackStatusBadge status={row.status} /></td>
                    <td className="py-3 text-muted-foreground">{row.created_at?.slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {meta.pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={meta.page <= 1} onClick={() => fetchItems(meta.page - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground self-center">Page {meta.page} of {meta.pages}</span>
              <Button variant="outline" size="sm" disabled={meta.page >= meta.pages} onClick={() => fetchItems(meta.page + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
